import React, { Fragment, useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

import Typography from "@mui/material/Typography";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";

import { emailPreferencesList, entityTypeDetails } from "./content";
import { INVITE_CODE_KEY, userPreferencesLocalStorageItem } from "../constant";
import { SNETButton, StyledDropdown } from "shared/dist/components";
import { onboardingRoutes } from "../Routes";
import { userEntities, userPreferenceTypes } from "../../../Utils/user";
import { inviteMembersActions, organizationActions } from "../../../Services/Redux/actionCreators";
import {
    onboardingActions,
    preferenceActions,
} from "../../../Services/Redux/actionCreators/userActions";
import LoginBanner from "./LoginBanner";
import InformationBox from "./InformationBox";
import { useLocation } from "react-router";
import classes from "./SingularityAccount.module.css";
import { useNavigateTo } from "../../../GlobalRouter/hook/NavigateTo";
import FormActions from "../../../Components/PageFormComponents/FormActions";
import FormContentCard from "../../../Components/PageFormComponents/FormContentCard";

const userEntitiesOptions = [
    {
        value: userEntities.ORGANIZATION,
        label: userEntities.ORGANIZATION,
    },
    {
        value: userEntities.INDIVIDUAL,
        label: userEntities.INDIVIDUAL,
    },
    { value: userEntities.INVITEE, label: "Accept Invitation" },
];

const SingularityAccount = () => {
    const navigateTo = useNavigateTo();
    const search = useLocation().search;

    const { entity, selectedOrganizationUuid, isOrgFoundInBlockchain } = useSelector(
        (state) => ({
            entity: state.user.entity,
            isOrgFoundInBlockchain: state.organization.foundInBlockchain,
            selectedOrganizationUuid: state.organization.selectedOrganizationUuid,
        }),
        shallowEqual
    );

    const [emailPreferences, setEmailPreferences] = useState({
        [userPreferenceTypes.FEATURE_RELEASE]: false,
        [userPreferenceTypes.WEEKLY_SUMMARY]: false,
        [userPreferenceTypes.COMMENTS_AND_MESSAGES]: false,
    });
    const [userEntity, setUserEntity] = useState(entity ? entity : userEntitiesOptions[0].value);
    const [inviteCode, setInviteCode] = useState();

    const dispatch = useDispatch();
    const userPreferencesInLocalStorage = localStorage.getItem(userPreferencesLocalStorageItem);

    useEffect(() => {
        if (userPreferencesInLocalStorage) {
            const { savedEmailChanges, savedEntity } = JSON.parse(userPreferencesInLocalStorage);
            setEmailPreferences(savedEmailChanges);
            setUserEntity(savedEntity);
        }
    }, [userPreferencesInLocalStorage]);

    useEffect(() => {
        const searchParams = new URLSearchParams(search);
        const inviteCode = searchParams.get(INVITE_CODE_KEY);

        if (inviteCode) {
            setInviteCode(inviteCode);
            setUserEntity(userEntities.INVITEE);
        }
    }, [search]);

    const verifiedInvitationCode = async () => {
        try {
            await dispatch(inviteMembersActions.verifyInvitation(inviteCode));
        } catch (err) {
            console.error(err);
        }
    };

    const handleContinue = async () => {
        if (userPreferencesInLocalStorage) {
            localStorage.removeItem(userPreferencesLocalStorageItem);
        }
        if (inviteCode) {
            await verifiedInvitationCode();
        }
        dispatch(onboardingActions.setUserEntity(userEntity));
        dispatch(preferenceActions.updateEmailPreferences(emailPreferences));
        navigateTo.onboarding(
            userEntity === userEntities.ORGANIZATION
                ? onboardingRoutes.ORGANIZATION.path
                : onboardingRoutes.INVITEE.path
        );
    };

    const handleCancel = async () => {
        dispatch(onboardingActions.setUserEntity(userEntities.DEFAULT));
        setEmailPreferences({
            [userPreferenceTypes.FEATURE_RELEASE]: false,
            [userPreferenceTypes.WEEKLY_SUMMARY]: false,
            [userPreferenceTypes.COMMENTS_AND_MESSAGES]: false,
        });
        dispatch(organizationActions.resetOrganizationDetailsData());
        if (!selectedOrganizationUuid) {
            return;
        }
        await dispatch(organizationActions.selectOrganization(selectedOrganizationUuid));
        isOrgFoundInBlockchain
            ? navigateTo.services({ orgUuid: selectedOrganizationUuid })
            : navigateTo.organizationSetup({ orgUuid: selectedOrganizationUuid });
    };

    const handleEntityChange = (event) => {
        const value = event.target.value;
        if (value === "default") {
            return;
        }
        setUserEntity(value);
        dispatch(onboardingActions.setUserEntity(value));
    };

    const handleEmailPreferencesChange = (event) => {
        const value = event.target.value;
        const updatedEmailPreferences = { ...emailPreferences, [value]: !emailPreferences[value] };
        setEmailPreferences(updatedEmailPreferences);
    };

    const shouldContinueBeDisabled = () => {
        let disableContinue = !userEntity || userEntity === userEntities.DEFAULT;
        return disableContinue;
    };

    return (
        <Fragment>
            <FormContentCard
                title={entityTypeDetails.title}
                description={entityTypeDetails.description}
            >
                <div className={classes.entityTypeSelector}>
                    <StyledDropdown
                        labelTxt="Please Select"
                        inputLabel="Entity Type"
                        value={userEntity}
                        list={userEntitiesOptions}
                        onChange={handleEntityChange}
                    />
                    {userEntity === userEntities.INVITEE && (
                        <Typography className={classes.entityTypeInvite}>
                            The Owner of an existing approved company entity will need to send you
                            an invitation through email.
                        </Typography>
                    )}
                </div>
                <InformationBox />
            </FormContentCard>
            <LoginBanner emailPreferences={emailPreferences} />
            <FormContentCard title="Email Preferences">
                <div className={classes.checkboxContainer}>
                    {emailPreferencesList.map((preference) => (
                        <FormControlLabel
                            key={preference.type}
                            control={
                                <Checkbox
                                    color="primary"
                                    checked={emailPreferences[preference.type]}
                                    value={preference.type}
                                    onChange={handleEmailPreferencesChange}
                                />
                            }
                            label={preference.description}
                        />
                    ))}
                </div>
            </FormContentCard>
            <FormActions className={classes.btnContainer}>
                <SNETButton
                    color="primary"
                    children="cancel"
                    variant="outlined"
                    onClick={handleCancel}
                />
                <SNETButton
                    color="primary"
                    children="continue"
                    variant="contained"
                    onClick={handleContinue}
                    disabled={shouldContinueBeDisabled()}
                />
            </FormActions>
        </Fragment>
    );
};

export default SingularityAccount;
